import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, LoadingSpinner, ViewPageHeader, Button, ResourcePreview } from '@components/common';
import { BaseModal } from '@components/modals';
import { useTranslation } from '@hooks/useTranslation';
import { useNotification } from '@hooks/useNotification';
import { formatDate } from '@utils/dateUtils';
import { ModelType } from '@constants/authorization';
import Icon from '@components/Icon/Icon';
import api from '@services/api';
import './ConsumptionViewPage.scss';

const ConsumptionViewPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const notify = useNotification();
    const [consumption, setConsumption] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalState, setModalState] = useState({ isOpen: false, mode: 'delete' });

    useEffect(() => {
        const fetchConsumption = async () => {
            try {
                setLoading(true);
                const response = await api.get(`/consumptions/${id}`);
                setConsumption(response.data);
                setError(null);
            } catch (err) {
                console.error('Failed to fetch consumption:', err);
                setError(t('consumptionView.fetchError'));
                notify.error(t('consumptionView.fetchError'));
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchConsumption();
        }
    }, [id, t]);

    const handleBack = () => {
        navigate('/consumptions');
    };

    const handleEdit = () => {
        navigate(`/consumptions/${id}/edit`);
    };

    const handleDelete = () => {
        setModalState({ isOpen: true, mode: 'delete' });
    };

    const handleModalClose = () => {
        setModalState({ isOpen: false, mode: 'delete' });
    };

    const handleModalSuccess = () => {
        if (modalState.mode === 'delete') {
            // Navigate to the resource view page if the consumption has a resource
            if (consumption?.resource?.id) {
                navigate(`/resources/${consumption.resource.id}`);
            } else {
                navigate('/consumptions');
            }
        }
    };

    if (loading) {
        return (
            <div className="consumption-view-page">
                <div className="loading-container">
                    <LoadingSpinner />
                    <p>{t('consumptionView.loading')}</p>
                </div>
            </div>
        );
    }

    if (error || !consumption) {
        return (
            <div className="consumption-view-page">
                <Card className="error-card">
                    <div className="error-content">
                        <h2>{t('consumptionView.errorTitle')}</h2>
                        <p>{error || t('consumptionView.notFound')}</p>
                    </div>
                </Card>
            </div>
        );
    }

    const unit = consumption.resource.resource_metadata?.find(meta => meta.metadata_key === 'unit')?.value;

    return (
        <div className="consumption-view-page">
            <ViewPageHeader
                onBack={handleBack}
                onEdit={handleEdit}
                onDelete={handleDelete}
                modelType={ModelType.CONSUMPTION}
                modelId={id}
            />

            <div className="consumption-details">
                <Card className="consumption-info-card" style={{ animation: 'slideInUp 0.6s ease-out' }}>
                    <div className="consumption-header">
                        <div className="consumption-icon">
                            <Icon icon="arrow-down" size="lg" color="#fe7474" />
                        </div>
                        <div className="consumption-info">
                            <h1>{t('consumptionView.consumptionDetails')}</h1>
                            <div className="quantity-consumed">
                                -{consumption.quantity_consumed} {unit ||t('consumptionView.unitsConsumed')}
                            </div>
                        </div>
                    </div>

                    {/* Resource Preview Section */}
                    {consumption.resource && (
                        <ResourcePreview
                            resource={consumption.resource}
                            noImageText={t('common.noImage')}
                        />
                    )}

                    <div className="consumption-metadata">
                        {consumption.resource && (
                            <div className="metadata-item">
                                <div className="metadata-icon">
                                    <Icon icon="cubes" size="sm" />
                                </div>
                                <span className="label">{t('consumptionView.resource')}:</span>
                                <span className="value">{consumption.resource.name}</span>
                            </div>
                        )}
                        {consumption.user && (
                            <div className="metadata-item">
                                <div className="metadata-icon">
                                    <Icon icon="arrow-down" size="sm" />
                                </div>
                                <span className="label">{t('consumptionView.consumer')}:</span>
                                <span className="value">{consumption.user.name || consumption.user.email}</span>
                            </div>
                        )}
                        <div className="metadata-item">
                            <div className="metadata-icon">
                                <Icon icon="calculator" size="sm" />
                            </div>
                            <span className="label">{t('consumptionView.quantityConsumed')}:</span>
                            <span className="value">{consumption.quantity_consumed}</span>
                        </div>
                        {consumption.consumed_at && (
                            <div className="metadata-item">
                                <div className="metadata-icon">
                                    <Icon icon="calendar" size="sm" />
                                </div>
                                <span className="label">{t('consumptionView.consumedAt')}:</span>
                                <span className="value">
                                    {formatDate(consumption.consumed_at, true, i18n.language, t)}
                                </span>
                            </div>
                        )}
                        {consumption.notes && (
                            <div className="metadata-item notes">
                                <div className="notes-header">
                                    <div className="metadata-icon">
                                        <Icon icon="file-alt" size="sm" />
                                    </div>
                                    <span className="label">{t('consumptionView.notes')}:</span>
                                </div>
                                <div className="value">{consumption.notes}</div>
                            </div>
                        )}
                    </div>
                </Card>
            </div>

            <BaseModal
                isOpen={modalState.isOpen}
                onClose={handleModalClose}
                onSuccess={handleModalSuccess}
                mode={modalState.mode}
                item={consumption}
                itemType="consumption"
                deleteFn={async (id) => await api.delete(`/consumptions/${id}`)}
                size="small"
            />
        </div>
    );
};

export default ConsumptionViewPage;
